import { FC, useEffect } from "react";
import { RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import useCashRegisterStore from "@/stores/cash-register";
import { updateShopTheme } from "@/helpers/css-variables";
import { useTranslation } from "react-i18next";
import "@/services/firebase";
import router from "@/helpers/router";
import { setDefaultOptions as setDefaultDateFnsOptions } from "date-fns";
import { enGB, de, nl } from "date-fns/locale";
import { Provider } from "react-redux";
import { store } from "./store";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  },
});

const App: FC = () => {
  const { current } = useCashRegisterStore();
  const {
    i18n: { language: currentLanguage },
  } = useTranslation();

  useEffect(() => {
    if (!!current) updateShopTheme(current);
  }, [current]);

  useEffect(() => {
    const parameters = new URLSearchParams(window.location.search);
    const kioskId = parameters.get("kiosk_id");
    if (kioskId) sessionStorage.setItem("kioskId", kioskId);
    const returnUrl = parameters.get("returnUrl");
    if (returnUrl) sessionStorage.setItem("returnUrl", returnUrl);
  }, []);

  useEffect(() => {
    sessionStorage.setItem("language", currentLanguage);
    const dateFnsLocale = { en: enGB, nl: nl, de: de }[currentLanguage];
    setDefaultDateFnsOptions({ locale: dateFnsLocale });
  }, [currentLanguage]);

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Provider>
  );
};

export default App;
